import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import './tap-result.css'

/**
 * The answer a learner tapped in a story email, shown over the story they landed in.
 * The email's tap link records the answer on the API, then sends them here signed in, so this
 * only has to celebrate it: `tap` is the question, `r` the result, `line` and `a` the quiz line
 * and its meaning (the app has no way to work those out on its own).
 */
const TapResult: React.FC = () => {
  const { t } = useTranslation('reader')
  const [params, setParams] = useSearchParams()
  const tap = params.get('tap')
  const [open, setOpen] = useState(!!tap)

  const result = params.get('r')
  const line = params.get('line') || ''
  const meaning = params.get('a') || ''

  // Clear the answer from the URL once it has been shown, so a refresh or a shared link does not
  // celebrate again. The timer below fires long after this render, so work from the params as they
  // are then rather than the ones this render closed over.
  const dismiss = () => {
    setOpen(false)
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        ;['tap', 'r', 'line', 'a'].forEach((k) => next.delete(k))
        return next
      },
      { replace: true },
    )
  }
  const dismissRef = useRef(dismiss)
  dismissRef.current = dismiss

  useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => dismissRef.current(), 12000)
    return () => clearTimeout(timer)
  }, [open])

  if (!open || !tap) return null

  // `line` is Chinese and `meaning` is the English the email carried: both shown as they come.
  const lineMeans = () => t('tapResult.lineMeans', { line, meaning })
  let emoji = '👍'
  let title = t('tapResult.thanksTitle')
  let detail = t('tapResult.thanksDetail')
  if (tap === 'quiz' && result === 'correct') {
    emoji = '🎉'
    title = t('tapResult.correctTitle')
    detail = meaning ? lineMeans() : t('tapResult.correctDetail')
  } else if (tap === 'quiz') {
    emoji = '💡'
    title = t('tapResult.wrongTitle')
    detail = meaning ? lineMeans() : t('tapResult.wrongDetail')
  } else if (tap === 'words_known') {
    emoji = '👀'
    title = t('tapResult.wordsKnownTitle')
    detail = t('tapResult.wordsKnownDetail')
  }

  const celebrate = tap === 'quiz' && result === 'correct'

  return (
    <div className={`tap-result${celebrate ? ' tap-result--celebrate' : ''}`} role='status'>
      {celebrate && (
        <div className='tap-result-confetti' aria-hidden='true'>
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} className={`tap-confetti tap-confetti-${i % 4}`} />
          ))}
        </div>
      )}
      <span className='tap-result-emoji' aria-hidden='true'>
        {emoji}
      </span>
      <div className='tap-result-text'>
        <p className='tap-result-title'>{title}</p>
        <p className='tap-result-detail'>{detail}</p>
      </div>
      <button type='button' className='tap-result-close' onClick={dismiss} aria-label={t('tapResult.close')}>
        ×
      </button>
    </div>
  )
}

export default TapResult
